import React from 'react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { TFunction } from 'i18next';
import { downloadMergeOrderPdf } from 'components/order-details/main-order-view/document-print-actions/files';
import { BatchAction } from 'components/orders/shared/batch-action-bar.component';

// Batch-printable order documents (merged into one PDF by denthub-documents) and the
// translation key used as the merged file's name. Keys are OrderDocumentType values.
export const BATCH_PDF_FILE_NAME_KEYS: Record<string, string> = {
    ORDER_SUMMARY_PDF: 'orders:ORDER_SUMMARIES',
    WORK_SHEET_V2_PDF: 'orders:ORDER_WORKSHEETS',
    WORK_SHEET_SUBCONTRACTOR_PDF: 'orders:ORDER_WORKSHEETS_SUBCONTRACTOR',
    DELIVERY_NOTE_PDF: 'organization:PDF_TEMPLATES.DELIVERY_NOTES',
    DESTINATION_ADDRESS_LABEL_PDF: 'orders:DELIVERY_ADDRESS_LABELS',
    PATIENT_LABEL_PDF: 'orders:PATIENT_LABELS',
};

// Menu order and label of each batch PDF action, shared by the Order List and the Workflow orders page.
const BATCH_PDF_ACTION_LABEL_KEYS: { type: string; labelKey: string }[] = [
    { type: 'ORDER_SUMMARY_PDF', labelKey: 'orders:PRINT_ORDER_SUMMARY' },
    { type: 'WORK_SHEET_V2_PDF', labelKey: 'orders:PRINT_WORKSHEET' },
    { type: 'WORK_SHEET_SUBCONTRACTOR_PDF', labelKey: 'orders:PRINT_WORKSHEET_SUBCONTRACTOR' },
    { type: 'DELIVERY_NOTE_PDF', labelKey: 'orders:PRINT_DELIVERY_NOTE' },
    { type: 'DESTINATION_ADDRESS_LABEL_PDF', labelKey: 'orders:PRINT_DELIVERY_ADDRESS_LABEL' },
    { type: 'PATIENT_LABEL_PDF', labelKey: 'orders:PRINT_PATIENT_LABEL' },
];

export const isBatchPdfActionType = (type: string): boolean => !!BATCH_PDF_FILE_NAME_KEYS[type];

export const getBatchPdfActions = (translate: TFunction): BatchAction[] =>
    BATCH_PDF_ACTION_LABEL_KEYS.map(({ type, labelKey }) => ({
        icon: <PictureAsPdfIcon />,
        name: translate(labelKey),
        type,
    }));

export type BatchPdfDisableContext = {
    isDentist: boolean;
    isExternalTechnician: boolean;
    isElevatedExternalUser: boolean;
    isExternalLabUsersEnabled: boolean;
    // True when any selected order has no generated delivery note PDF yet.
    hasMissingDeliveryNote: boolean;
};

export const isBatchPdfActionDisabled = (
    type: string,
    {
        isDentist,
        isExternalTechnician,
        isElevatedExternalUser,
        isExternalLabUsersEnabled,
        hasMissingDeliveryNote,
    }: BatchPdfDisableContext,
): boolean => {
    switch (type) {
        case 'WORK_SHEET_V2_PDF':
            return isExternalTechnician || isDentist;
        case 'WORK_SHEET_SUBCONTRACTOR_PDF':
            // Same audience as the print action on the order page: external technicians,
            // or lab users whose lab has external lab users enabled.
            return isDentist || !(isExternalTechnician || isExternalLabUsersEnabled);
        case 'DELIVERY_NOTE_PDF':
            // DD-3659: the elevated external tier ships the orders shared with them,
            // so they get the delivery note too; the lower external tiers do not.
            return (isExternalTechnician && !isElevatedExternalUser) || hasMissingDeliveryNote;
        case 'DESTINATION_ADDRESS_LABEL_PDF':
            // DD-3781: a lab-side shipping task; of the external tiers only the elevated one ships.
            return isDentist || (isExternalTechnician && !isElevatedExternalUser);
        case 'PATIENT_LABEL_PDF':
            // DD-3781: the order page never offers the patient label to external users.
            return isDentist || isExternalTechnician;
        default:
            return false;
    }
};

export const downloadBatchOrdersPdf = async (orderIds: string[], type: string, translate: TFunction): Promise<void> => {
    const isoDateString = new Date().toISOString().substring(0, 10);
    const fileName = `${translate(BATCH_PDF_FILE_NAME_KEYS[type])}-${isoDateString}.pdf`;
    await downloadMergeOrderPdf(orderIds, type, fileName);
};
